import mapboxgl from 'mapbox-gl';
import {
  MAPBOX_STYLE,
  MELBOURNE_COORDINATES,
  MARKER_COLORS,
  POINTS_SOURCE,
  POINTS_CIRCLE_LAYER,
  POINTS_SYMBOL_LAYER,
  AUTO_PATH_SOURCE,
  AUTO_PATH_LAYER,
  CUSTOM_PATH_SOURCE,
  CUSTOM_PATH_LAYER,
  DISTANCE_LABELS_SOURCE,
  DISTANCE_LABELS_LAYER,
  PATH_STYLES
} from './constants';
import { LocationType } from '../Sidebar/LocationList';
import { setupMarkerHoverEvents } from './MapMarkers';
import { mapDebug } from '../utils/debug';

// Initialize map with all required map layers
export const initializeMap = ({
  mapContainer,
  setMapError,
  setMapReady,
  sourcesReadyRef,
  shouldShowDistanceLabels
}) => {
  if (!mapContainer?.current) {

    return null;
  }
  
  try {
    // Initialize map
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('mapbox_token') || process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
    
    if (!token) {
      console.error('Missing Mapbox access token');
      setMapError('Missing Mapbox access token. Please add ?mapbox_token=your_token to URL or set REACT_APP_MAPBOX_ACCESS_TOKEN in .env');
      return null;
    }
    
    mapboxgl.accessToken = token;
    
    // Create map instance
    const map = new mapboxgl.Map({
      container: mapContainer.current,
      style: MAPBOX_STYLE,
      center: [MELBOURNE_COORDINATES.lng, MELBOURNE_COORDINATES.lat],
      zoom: 10,
      attributionControl: false
    });
    
    // Expose a global reloadMap function for debugging
    window.reloadMap = (pathData) => {
      
      if (!map) {

        return;
      }
      
      if (!sourcesReadyRef.current) {

        return;
      }
      
      try {
        if (pathData && map.getSource(AUTO_PATH_SOURCE)) {

          map.getSource(AUTO_PATH_SOURCE).setData(pathData);
          
          if (map.getLayer(AUTO_PATH_LAYER)) {
            map.setLayoutProperty(AUTO_PATH_LAYER, 'visibility', 'visible');
          }
          
          map.triggerRepaint();

          return; 
        }
      } catch (err) {

      }
      
      try {

        if (map.getLayer(AUTO_PATH_LAYER)) {

          map.removeLayer(AUTO_PATH_LAYER);
        }
        
        if (map.getLayer(CUSTOM_PATH_LAYER)) {

          map.removeLayer(CUSTOM_PATH_LAYER);
        }
        
        if (map.getSource(AUTO_PATH_SOURCE)) {

          map.removeSource(AUTO_PATH_SOURCE);
        }
        
        if (map.getSource(CUSTOM_PATH_SOURCE)) {

          map.removeSource(CUSTOM_PATH_SOURCE);
        }

        map.addSource(AUTO_PATH_SOURCE, {
          type: 'geojson',
          data: pathData || {
            type: 'Feature',
            properties: {},
            geometry: { type: 'LineString', coordinates: [] }
          }
        });

        map.addSource(CUSTOM_PATH_SOURCE, {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: {},
            geometry: { type: 'LineString', coordinates: [] }
          }
        });

        map.addLayer({
          id: AUTO_PATH_LAYER,
          type: 'line',
          source: AUTO_PATH_SOURCE,
          layout: {
            'line-join': 'round',
            'line-cap': 'round',
            'visibility': 'visible'
          },
          paint: {
            // Use dynamic color from feature properties, fallback to default
            'line-color': [
              'case',
              ['has', 'route_color'],
              ['get', 'route_color'],
              PATH_STYLES.auto.color // Default color
            ],
            'line-width': PATH_STYLES.auto.width,
            'line-opacity': PATH_STYLES.auto.opacity
          }
        }, POINTS_CIRCLE_LAYER); // Add BEFORE the points layer

        map.addLayer({
          id: CUSTOM_PATH_LAYER,
          type: 'line',
          source: CUSTOM_PATH_SOURCE,
          layout: {
            'line-join': 'round',
            'line-cap': 'round',
            'visibility': 'visible'
          },
          paint: {
            'line-color': PATH_STYLES.custom.color,
            'line-width': PATH_STYLES.custom.width,
            'line-opacity': PATH_STYLES.custom.opacity,
            'line-dasharray': [2, 1] // Dashed line for custom path
          }
        }, POINTS_CIRCLE_LAYER); // Add BEFORE the points layer
        
        map.triggerRepaint();

      } catch (err) {

        // Try one final fallback method - create unique IDs
        try {

          const timestamp = Date.now();
          const autoLayerId = `${AUTO_PATH_LAYER}-${timestamp}`;
          const autoSourceId = `${AUTO_PATH_SOURCE}-${timestamp}`;
          
          map.addSource(autoSourceId, {
            type: 'geojson',
            data: pathData || {
              type: 'Feature',
              properties: {},
              geometry: { type: 'LineString', coordinates: [] }
            }
          });
          
          map.addLayer({
            id: autoLayerId,
            type: 'line',
            source: autoSourceId,
            layout: {
              'line-join': 'round',
              'line-cap': 'round',
              'visibility': 'visible'
            },
            paint: {
              'line-color': PATH_STYLES.auto.color,
              'line-width': PATH_STYLES.auto.width,
              'line-opacity': PATH_STYLES.auto.opacity
            }
          }, POINTS_CIRCLE_LAYER);

        } catch (fallbackErr) {

        }
      }
    };
    
    map.addControl(new mapboxgl.NavigationControl(), 'bottom-right');
    
    // Use a safely referenced variable to track map instance
    let mapRef = map;
    
    // Listen for container size changes to resize map
    const resizeObserver = new ResizeObserver(() => {
      if (mapRef) {
        try {
          // Check if map container exists and is connected to DOM
          if (mapContainer.current && document.body.contains(mapContainer.current)) {
            mapRef.resize();
          }
        } catch (error) {

        }
      }
    });
    
    if (mapContainer.current) {
      resizeObserver.observe(mapContainer.current);
    }
    
    // When map loads, initialize data sources and layers
    map.on('load', () => {
      try {
        initializeMapLayers(map, shouldShowDistanceLabels);
        setupMarkerHoverEvents(map);
        
        sourcesReadyRef.current = true;
        setMapReady(true);
      } catch (err) {
        console.error('Error initializing map layers:', err);
        setMapError("Error initializing map data sources and layers: " + err.message);
      }
    });
    
    // Cleanup function to ensure map reference is cleared
    const cleanup = () => {
      mapRef = null;
    };
    
    return { map, resizeObserver, cleanup };
  } catch (error) {
    console.error('[TSP-MAP] Critical error during map initialization:', error);
    setMapError("Error initializing map: " + error.message);
    return null;
  }
};

// Initialize map layers
export const initializeMapLayers = (map, shouldShowDistanceLabels) => {
  if (!map) {
    console.warn('[TSP-MAP] initializeMapLayers called with null map');
    return;
  }
  
  // 1. Add point data source
  map.addSource(POINTS_SOURCE, {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: []
    }
  });
  
  // 2. Add point circle layer
  map.addLayer({
    id: POINTS_CIRCLE_LAYER,
    type: 'circle',
    source: POINTS_SOURCE,
    paint: {
      // Circle color - set based on TP type (supply/demand) or fallback to location type
      'circle-color': [
        'case',
        ['==', ['get', 'tpType'], 'supply'], MARKER_COLORS[LocationType.START],      // Supply - Green
        ['==', ['get', 'tpType'], 'demand'], MARKER_COLORS[LocationType.WAYPOINT],   // Demand - Blue
        ['==', ['get', 'type'], LocationType.START], MARKER_COLORS[LocationType.START],
        ['==', ['get', 'type'], LocationType.END], MARKER_COLORS[LocationType.END],
        ['==', ['get', 'type'], LocationType.WAYPOINT], MARKER_COLORS[LocationType.WAYPOINT],
        ['==', ['get', 'isStart'], true], MARKER_COLORS[LocationType.START],
        MARKER_COLORS[LocationType.NORMAL]
      ],
      // Circle size - using VRP scaling mechanism
      'circle-radius': [
        'interpolate',
        ['linear'],
        ['zoom'],
        8, ['case', ['==', ['get', 'isStart'], true], 4, 3],    // Very zoomed out: small dots
        10, ['case', ['==', ['get', 'isStart'], true], 6, 5],   // City level: medium dots
        12, ['case', ['==', ['get', 'isStart'], true], 18, 16], // District level: full circles with numbers
        15, ['case', ['==', ['get', 'isStart'], true], 20, 18], // Street level: larger circles
        18, ['case', ['==', ['get', 'isStart'], true], 22, 20]  // Building level: largest circles
      ],
      // Stroke width
      'circle-stroke-width': 2,
      'circle-stroke-color': '#ffffff',
      'circle-opacity': 0.9
    }
  });
  
  // 3. Add point symbol layer - numbers displayed directly on points
  map.addLayer({
    id: POINTS_SYMBOL_LAYER,
    type: 'symbol',
    source: POINTS_SOURCE,
    layout: {
      'text-field': ['get', 'number'],
      'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
      // Text size - using VRP scaling mechanism (hidden at low zoom)
      'text-size': [
        'interpolate',
        ['linear'],
        ['zoom'],
        8, 0,   // Zoom 8: no text (invisible)
        10, 0,  // Zoom 10: no text (invisible)
        12, ['case', ['==', ['get', 'isStart'], true], 16, 14], // Zoom 12+: show numbers
        15, ['case', ['==', ['get', 'isStart'], true], 18, 16], // Zoom 15+: larger text
        18, ['case', ['==', ['get', 'isStart'], true], 20, 18]  // Zoom 18+: largest text
      ],
      'text-allow-overlap': true,
      'text-ignore-placement': true,
      'text-anchor': 'center', // Ensure text is centered on point
      'text-offset': [0, 0],   // Avoid any offset
      'text-justify': 'center' // Text internal alignment
    },
    paint: {
      'text-color': '#ffffff',
      // Text opacity - using VRP scaling mechanism (fully hidden at low zoom levels)
      'text-opacity': [
        'interpolate',
        ['linear'],
        ['zoom'],
        8, 0,    // Zoom 8: completely hidden
        10, 0,   // Zoom 10: completely hidden
        11, 0,   // Zoom 11: completely hidden
        12, 1.0, // Zoom 12+: fully visible
        18, 1.0  // Zoom 18: fully visible
      ],
      // Remove text halo for cleaner look inside circles (VRP style)
      'text-halo-width': 0
    }
  });
  
  // 4. Add auto path line data source
  map.addSource(AUTO_PATH_SOURCE, {
    type: 'geojson',
    data: {
      type: 'Feature',
      properties: {
        pathType: 'auto'
      },
      geometry: {
        type: 'LineString',
        coordinates: []
      }
    }
  });
  
  // 5. Add auto path line layer with dynamic color support
  map.addLayer({
    id: AUTO_PATH_LAYER,
    type: 'line',
    source: AUTO_PATH_SOURCE,
    layout: {
      'line-join': 'round',
      'line-cap': 'round',
      'visibility': 'visible' // Initially set to visible, controlled by separate useEffect
    },
    paint: {
      // Use dynamic color from feature properties, fallback to default
      'line-color': [
        'case',
        ['has', 'route_color'],
        ['get', 'route_color'],
        PATH_STYLES.auto.color // Default color
      ],
      // Responsive auto path line width
      'line-width': [
        'interpolate',
        ['linear'],
        ['zoom'],
        8, 2,    // City level: thin lines
        10, 3,   // Medium level
        12, 4,   // Default level: normal lines
        15, 5,   // Street level: thick lines
        18, 7    // Building level: thickest lines
      ],
      'line-opacity': PATH_STYLES.auto.opacity,
      'line-dasharray': [1, 0] // Auto path uses solid line
    }
  }, POINTS_CIRCLE_LAYER); // Add BEFORE the points layer to ensure points are on top
  
  // 6. Add custom path line data source
  map.addSource(CUSTOM_PATH_SOURCE, {
    type: 'geojson',
    data: {
      type: 'Feature',
      properties: {
        pathType: 'custom'
      },
      geometry: {
        type: 'LineString',
        coordinates: []
      }
    }
  });
  
  // 7. Add custom path line layer
  map.addLayer({
    id: CUSTOM_PATH_LAYER,
    type: 'line',
    source: CUSTOM_PATH_SOURCE,
    layout: {
      'line-join': 'round',
      'line-cap': 'round',
      'visibility': 'visible' // Initially set to visible, controlled by separate useEffect
    },
    paint: {
      'line-color': PATH_STYLES.custom.color,
      // Responsive custom path line width
      'line-width': [
        'interpolate',
        ['linear'],
        ['zoom'],
        8, 1.5,  // City level: thin lines
        10, 2.5, // Medium level
        12, 3,   // Default level: normal lines
        15, 4,   // Street level: thick lines
        18, 6    // Building level: thickest lines
      ],
      'line-opacity': PATH_STYLES.custom.opacity,
      // Static dash pattern (dasharray cannot be interpolated)
      'line-dasharray': [2, 2]
    }
  }, POINTS_CIRCLE_LAYER); // Add BEFORE the points layer to ensure points are on top
  
  // 8. Add distance label data source
  map.addSource(DISTANCE_LABELS_SOURCE, {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: []
    }
  });
  
  // 9. Add distance label layer
  map.addLayer({
    id: DISTANCE_LABELS_LAYER,
    type: 'symbol',
    source: DISTANCE_LABELS_SOURCE,
    layout: {
      'text-field': ['get', 'distance'],
      'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
      // Responsive text size - using VRP scaling mechanism
      'text-size': [
        'interpolate',
        ['linear'],
        ['zoom'],
        10, 0,  // Zoom 10: no text (invisible)
        11, 12, // Zoom 11: small text (district level)
        12, 14, // Zoom 12: normal text
        15, 16, // Zoom 15: larger text
        18, 18  // Zoom 18: largest text
      ],
      'text-allow-overlap': true,
      'text-ignore-placement': true,
      'symbol-placement': 'point',
      'text-justify': 'center',
      'text-anchor': 'center',
      'visibility': shouldShowDistanceLabels ? 'visible' : 'none'
    },
    paint: {
      'text-color': '#000000',
      'text-halo-width': 3,
      'text-halo-color': 'rgba(255,255,255,0.95)',
      // Responsive opacity - using VRP scaling mechanism (auto show at district level)
      'text-opacity': [
        'interpolate',
        ['linear'],
        ['zoom'],
        8, 0,   // Zoom 8: completely hidden
        9, 0,   // Zoom 9: completely hidden
        10, 0,  // Zoom 10: completely hidden (city level)
        11, 0.8, // Zoom 11: show at district level
        12, 0.9, // Zoom 12+: fully visible
        18, 0.9  // Zoom 18: fully visible
      ]
    }
  });
}; 